import * as React from "react";
import { LifeBuoy, Power, StepForward, Plus } from "lucide-react";
import { FaDigitalOcean } from "react-icons/fa6";
import { useLocation } from "react-router-dom";

import { NavMain } from "@/components/nav-main";
import { type NodeType } from "@/config/nodes";
import { NavUser } from "@/components/nav-user";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { FeedbackModal } from "@/components/feedback-modal";
import { UserContext } from "@/context/user";
import { ProjectContext } from "@/context/project";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, logout } = React.useContext(UserContext);
  const { projectsMap, activeProject, isDigitalOceanConnected, checkDigitalOceanConnection } = React.useContext(ProjectContext);
  // const { open } = useSidebar();
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  // const location = useLocation();

  const handleFeedbackSubmit = async ({
    type,
    message,
  }: {
    type: string;
    message: string;
  }) => {
    const idUrl = import.meta.env.VITE_PUBLIC_API_ENDPOINT;
    try {
      const response = await fetch(`${idUrl}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type,
          message,
        }),
        credentials: "include",
      });
      if (!response.ok) {
        // You can add error handling here, like showing a toast notification.
        console.error("Failed to submit feedback");
      }
    } catch (error) {
      // You can add error handling here, like showing a toast notification.
      console.error("An error occurred while submitting feedback:", error);
    }
  };

  const handleDigitalOceanLogout = async () => {
    if (!activeProject?.id) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/auth/digitalocean/logout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ projectId: activeProject.id }),
          credentials: "include",
        }
      );
      if (response.ok) {
        await checkDigitalOceanConnection();
      }
    } catch (error) {
      console.error("DigitalOcean logout failed:", error);
    }
  };

  const projects = Object.values(projectsMap);
  const activeProjectId = activeProject?.id;

  const secondaryNav = [
    {
      name: "Support",
      icon: LifeBuoy,
      onClick: () => setFeedbackModalOpen(true),
    },
  ];



  const displayName =
    user?.data.name || user?.data.display_name || user?.data.email || "User";

  return (
    <>
      <Sidebar collapsible="icon" className="!top-16 !h-[calc(100vh-4rem)] border-r-0" {...props}>
        <SidebarHeader className="relative h-8 w-full pb-0 pt-0 my-2">
          <SidebarTrigger className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8" />
        </SidebarHeader>
        <SidebarSeparator className="my-0 transition-all duration-200 group-data-[state=expanded]:opacity-0 overflow-hidden" />
        <SidebarContent className="flex flex-col h-full overflow-hidden">
          {projects.length > 0 ? (
            <div className="flex flex-1 flex-col overflow-hidden gap-6">
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {activeProjectId ? (
                  <div className="flex flex-col h-full overflow-hidden">
                    <div className="shrink-0">
                      <NavMain
                        label={"Project"}
                        items={[
                          {
                            name: "Pipeline",
                            isActive: false,
                            url: `/projects/${activeProjectId}`,
                            icon: StepForward,
                          },
                        ]}
                      />
                      <SidebarSeparator className="my-0 transition-all duration-200 group-data-[state=expanded]:opacity-0 overflow-hidden" />
                    </div>
                    
                    <div className="shrink-0">
                      <SidebarGroup className="!pb-0">
                        <SidebarGroupLabel>Nodes</SidebarGroupLabel>
                      </SidebarGroup>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0 [-ms-overflow-style:none] [scrollbar-width:none] hover:[scrollbar-width:thin] [&::-webkit-scrollbar]:w-0 hover:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-track]:bg-transparent transition-all">
                      <NavMain
                        items={activeProject?.apps
                          ?.filter((app) => app.type)
                          ?.map((app) => ({
                            name: app.name,
                            url: `/projects/${activeProject?.id}/${app.name}`,
                            type: app.type as NodeType,
                          }))}
                      />
                    </div>

                    {isDigitalOceanConnected && (
                      <div className="shrink-0">
                        <SidebarSeparator className="my-0 transition-all duration-200 group-data-[state=expanded]:opacity-0 overflow-hidden" />
                        <NavMain
                          label={"Integrations"}
                          items={[
                            {
                              name: "DigitalOcean",
                              icon: () => <FaDigitalOcean className="text-[#0080FF]" />,
                              items: [
                                {
                                  name: "Disconnect",
                                  url: "#",
                                  icon: Power,
                                  type: "service" as NodeType,
                                  onClick: handleDigitalOceanLogout,
                                }
                              ]
                            }
                          ]}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="shrink-0 mt-auto">
            <NavMain
              items={[
                {
                  name: "New Project",
                  url: "/new",
                  icon: Plus,
                  variant: "primary",
                },
              ]}
            />
            <SidebarSeparator className="my-0 transition-all duration-200 group-data-[state=expanded]:opacity-0 overflow-hidden" />
            <NavMain label={"Settings"} items={secondaryNav} />
          </div>
        </SidebarContent>
        <SidebarFooter>
          {user && (
            <NavUser
              user={{
                ...user.data,
                name: displayName,
              }}
              logout={logout}
            />
          )}
        </SidebarFooter>
      </Sidebar>
      <FeedbackModal
        open={feedbackModalOpen}
        onOpenChange={setFeedbackModalOpen}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
}
